// lcdcash-frontend/src/pages/EnvoiePage.jsx
import * as React from 'react';
import {
  Box, Container, Typography, Paper, Stack, Grid, Chip, Alert,
  List, ListItem, ListItemText, Button
} from '@mui/material';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Logo UPS
import UPSLogo from '../assets/logo/ups.png';

// Images étapes
import step1Img from '../assets/images/carton-bulle.jpg';
import step2Img from '../assets/images/tel-bulle.png';
import step3Img from '../assets/images/emballage-organise.png';
import step4Img from '../assets/images/UPS-envoie.png';

// i18n
import { useI18n } from '../i18n/I18nProvider';
import envoieI18n from '../i18n/pages/envoiepage.i18n';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';
const BLUE_BG = 'rgba(11,95,255,0.08)';

function CenteredImage({ src, alt, maxWidth = 440 }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        draggable={false}
        sx={{
          width: '100%',
          maxWidth,
          borderRadius: 2,
          height: 'auto',
          boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
        }}
      />
    </Box>
  );
}

export default function EnvoiePage() {
  const { t, register } = useI18n();
  React.useEffect(() => { register('envoie', envoieI18n); }, [register]);

  return (
    <>
      <Navbar />

      {/* HERO (aligné à gauche) */}
      <Box sx={{ bgcolor: BLUE, color: '#fff', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography
            component="h1"
            sx={{ fontWeight: 900, fontSize: { xs: 26, md: 36 }, lineHeight: 1.15, mb: 1 }}
          >
            {t('envoie.hero.title')}
          </Typography>
          <Typography sx={{ opacity: 0.95, fontSize: { xs: 15.5, md: 17 } }}>
            {t('envoie.hero.desc')}
          </Typography>

          <Alert
            icon={false}
            sx={{ mt: 2, bgcolor: '#fff', color: BLUE, borderRadius: 2, fontWeight: 600 }}
          >
            {t('envoie.hero.alert')}
          </Alert>
        </Container>
      </Box>

      {/* INTRO (aligné à gauche) */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
          <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
            {t('envoie.intro.text')}
          </Typography>
        </Container>
      </Box>

      {/* ÉTAPES — empilées, mêmes largeurs visuelles */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 5 } }}>
          <Stack spacing={2.5}>
            {/* Étape 1 */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip label="1" sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 800 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                  {t('envoie.steps.1.title')}
                </Typography>
              </Stack>
              <Typography sx={{ color: 'text.secondary', mb: 1.5 }}>
                {t('envoie.steps.1.body')}
              </Typography>
              <CenteredImage src={step1Img} alt={t('envoie.steps.1.alt')} />
            </Paper>

            {/* Étape 2 */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip label="2" sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 800 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                  {t('envoie.steps.2.title')}
                </Typography>
              </Stack>
              <Typography sx={{ color: 'text.secondary', mb: 1.5 }}>
                {t('envoie.steps.2.body')}
              </Typography>
              <CenteredImage src={step2Img} alt={t('envoie.steps.2.alt')} />
            </Paper>

            {/* Étape 3 */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip label="3" sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 800 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                  {t('envoie.steps.3.title')}
                </Typography>
              </Stack>
              <Typography sx={{ color: 'text.secondary', mb: 1.5 }}>
                {t('envoie.steps.3.body')}
              </Typography>
              <CenteredImage src={step3Img} alt={t('envoie.steps.3.alt')} />
              <Alert icon={false} sx={{ mt: 1.5, bgcolor: BLUE_BG }}>
                {t('envoie.steps.3.note')}
              </Alert>
            </Paper>

            {/* Étape 4 */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip label="4" sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 800 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                  {t('envoie.steps.4.title')}
                </Typography>
              </Stack>
              <Typography sx={{ color: 'text.secondary', mb: 1.5 }}>
                {t('envoie.steps.4.body')}
              </Typography>
              <CenteredImage src={step4Img} alt={t('envoie.steps.4.alt')} />
            </Paper>
          </Stack>
        </Container>
      </Box>

      {/* UPS — INFOS */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 5 } }}>
          <Paper
            variant="outlined"
            sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, borderColor: '#e8efe9', background: BLUE_BG }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2}>
              <Box
                component="img"
                src={UPSLogo}
                alt="UPS"
                sx={{ width: 50, height: 'auto', objectFit: 'contain' }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                  {t('envoie.UPS.title')}
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {t('envoie.UPS.text')}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>

      {/* CHECKLIST / À ÉVITER / BONNES PRATIQUES */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 5 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2.5,
              width: '100%',
            }}
          >
            {/* Checklist */}
            <Paper variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ bgcolor: BLUE_BG, p: 1.5 }}>
                <Typography sx={{ fontWeight: 900, textAlign: 'center', color: BLUE }}>
                  {t('envoie.panels.checklist.title')}
                </Typography>
              </Box>
              <Box sx={{ p: 2, flex: 1 }}>
                <List dense disablePadding>
                  {[
                    t('envoie.panels.checklist.i1'),
                    t('envoie.panels.checklist.i2'),
                    t('envoie.panels.checklist.i3'),
                    t('envoie.panels.checklist.i4'),
                    t('envoie.panels.checklist.i5'),
                    t('envoie.panels.checklist.i6'),
                    t('envoie.panels.checklist.i7'),
                  ].map((txt, idx) => (
                    <ListItem key={idx} sx={{ py: 0.4 }}>
                      <ListItemText primary={txt} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>

            {/* À éviter */}
            <Paper variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ bgcolor: BLUE_BG, p: 1.5 }}>
                <Typography sx={{ fontWeight: 900, textAlign: 'center', color: BLUE }}>
                  {t('envoie.panels.avoid.title')}
                </Typography>
              </Box>
              <Box sx={{ p: 2, flex: 1 }}>
                <List dense disablePadding>
                  {[
                    t('envoie.panels.avoid.i1'),
                    t('envoie.panels.avoid.i2'),
                    t('envoie.panels.avoid.i3'),
                    t('envoie.panels.avoid.i4'),
                  ].map((txt, idx) => (
                    <ListItem key={idx} sx={{ py: 0.4 }}>
                      <ListItemText primary={txt} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>

            {/* Bonnes pratiques */}
            <Paper variant="outlined" sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ bgcolor: BLUE_BG, p: 1.5 }}>
                <Typography sx={{ fontWeight: 900, textAlign: 'center', color: BLUE }}>
                  {t('envoie.panels.best.title')}
                </Typography>
              </Box>
              <Box sx={{ p: 2, flex: 1 }}>
                <List dense disablePadding>
                  {[
                    t('envoie.panels.best.i1'),
                    t('envoie.panels.best.i2'),
                    t('envoie.panels.best.i3'),
                  ].map((txt, idx) => (
                    <ListItem key={idx} sx={{ py: 0.4 }}>
                      <ListItemText primary={txt} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* FAQ (alignée à gauche) */}
      <Box sx={{ bgcolor: '#fff' }}>
        <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 5 } }}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>{t('envoie.faq.title')}</Typography>
            <Stack spacing={1.25}>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{t('envoie.faq.q1')}</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {t('envoie.faq.a1')}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{t('envoie.faq.q2')}</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {t('envoie.faq.a2')}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{t('envoie.faq.q3')}</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {t('envoie.faq.a3')}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                href="/tarifs"
                variant="contained"
                sx={{ bgcolor: BLUE, fontWeight: 700, textTransform: 'none', '&:hover': { backgroundColor: BLUE_HOVER } }}
              >
                {t('envoie.faq.cta')}
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Footer />
    </>
  );
}